/* RESPONSIBLE TEAM: team-standalone */
import Model, { attr } from '@ember-data/model';
import { type ATTRIBUTE_DESCRIPTOR_TYPES } from 'embercom/models/custom-objects/constants/attribute-descriptor-types';
import type Attribute from 'embercom/models/attribute';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { postRequest } from 'embercom/lib/inbox/requests';
import { action } from '@ember/object';
import { IntegrationType } from 'embercom/objects/standalone/constants';
import type ConversationAttributeDescriptor from 'embercom/models/conversation-attributes/descriptor';

export type IntercomFieldType = 'conversation' | 'user' | 'company';

export default class ExternalFieldIdentifier extends Model {
  @service declare finStandaloneService: FinStandaloneService;

  @attr('boolean') declare builtIn: boolean;
  @attr('boolean') declare canBeSynchronized: boolean;
  @attr('string') declare controllerFieldExternalId: string;
  @attr('string') declare externalId: string;
  @attr('string') declare intercomAttributeIdentifier: string;
  @attr('string') declare intercomDataType: ATTRIBUTE_DESCRIPTOR_TYPES;
  @attr('string') declare intercomFieldType: IntercomFieldType;
  @attr('string') declare name: string;
  @attr('boolean') declare disabledForIdentityMapping: boolean;
  @attr('boolean') declare hiddenForIdentityMapping: boolean;
  @attr('boolean') declare suitableForIdentityMapping: boolean;
  @attr('boolean') declare synchronized: boolean;
  @attr('boolean') declare required: boolean;
  @attr('boolean') declare updateable: boolean;
  @attr('boolean') declare createable: boolean;

  @attr('boolean') declare isSynchronizing?: boolean;

  get attributeModel(): Attribute | ConversationAttributeDescriptor | undefined {
    if (!this.intercomAttributeIdentifier) {
      return;
    }

    if (this.isUserField || this.isCompanyField) {
      return this.store.peekAll('attribute').findBy('identifier', this.intercomAttributeIdentifier);
    }

    if (this.isConversationField) {
      return this.store
        .peekAll('conversation-attributes/descriptor')
        .findBy('id', this.intercomAttributeIdentifier.split('.')[2]);
    }

    return;
  }

  get intercomCdaIdentifier() {
    if (
      !this.attributeModel ||
      !('isCustomData' in this.attributeModel) ||
      !this.attributeModel?.isCustomData
    ) {
      return;
    }
    return this.attributeModel?.identifier;
  }

  get intercomCvdaIdentifier() {
    if (!this.isConversationField) {
      return;
    }
    return this.attributeModel?.id;
  }

  get intercomSdaIdentifier() {
    if (
      !this.attributeModel ||
      !('isCustomData' in this.attributeModel) ||
      this.attributeModel?.isCustomData
    ) {
      return;
    }
    return this.attributeModel?.identifier;
  }

  get isCompanyField() {
    return this.intercomFieldType === 'company';
  }

  get isConversationField() {
    return this.intercomFieldType === 'conversation';
  }

  get isUserField() {
    return this.intercomFieldType === 'user';
  }

  get description() {
    return this.attributeModel?.description;
  }

  get type() {
    return this.intercomDataType;
  }

  get intercomAttributeDescriptorIcon() {
    switch (this.type) {
      case 'boolean':
        return 'boolean';
      case 'integer':
      case 'decimal':
        return 'numbers';
      case 'datetime':
      case 'date':
        return 'calendar';
      case 'list':
        return 'list';
      default:
        return 'small-text';
    }
  }

  @action
  async synchronize() {
    let appId = this.finStandaloneService.app.id;
    let integration = this.selectIntegrationForSync()!;

    await postRequest(
      `/ember/standalone/integrations/${integration.integrationType}/${integration.integrationId}/synchronize_field?app_id=${appId}`,
      {
        app_id: appId,
        id: this.externalId,
        intercom_field_type: this.intercomFieldType,
      },
    );
    this.synchronized = true;
  }

  private selectIntegrationForSync() {
    let validIntegrations = this.finStandaloneService.standaloneIntegrations.filter(
      (i) => i.integrationType !== IntegrationType.IntercomMessenger,
    );

    return (
      validIntegrations.find((i) => i.productionIntegration) ??
      validIntegrations.find((i) => i.defaultIntegration) ??
      validIntegrations[0]
    );
  }
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'standalone/external-field-identifier': ExternalFieldIdentifier;
  }
}
